import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, SlidersHorizontal, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";

export default function Settings() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [scale, setScale] = useState(5);
  const isAdmin = user?.role === "tenant_admin";

  const load = async () => {
    try { const { data } = await api.get("/rating-metrics"); setMetrics(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await api.post("/rating-metrics", { name, scale: Number(scale) }); toast.success("Metric added"); setName(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/rating-metrics/${id}`); toast.success("Metric removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">Rating Metrics</h1>
        <p className="text-[#86868B] mt-1">Standard criteria your team taps to rate every candidate.</p>
      </div>

      {isAdmin && (
        <form onSubmit={add} className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-5 mb-6 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-semibold">Metric name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="metric-name" placeholder="e.g. Leadership"
              className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors" />
          </div>
          <div className="w-28">
            <label className="text-sm font-semibold">Scale</label>
            <select value={scale} onChange={(e) => setScale(e.target.value)} data-testid="metric-scale"
              className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-3 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors">
              <option value={5}>1–5</option>
              <option value={10}>1–10</option>
            </select>
          </div>
          <button type="submit" data-testid="add-metric-btn"
            className="bg-[#007AFF] text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#0051FF] active:scale-95 transition-all flex items-center gap-2">
            <Plus size={18} /> Add
          </button>
        </form>
      )}

      {loading ? <div className="text-[#86868B]">Loading…</div> : metrics.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
          <SlidersHorizontal size={40} className="mx-auto text-[#C7C7CC] mb-3" strokeWidth={1.5} />
          <p className="font-semibold">No metrics yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {metrics.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid="metric-row"
              className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFCC00]/15 flex items-center justify-center">
                <Star size={18} className="text-[#D4AA00] fill-[#FFCC00]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold tracking-tight">{m.name}</h3>
                <p className="text-xs text-[#86868B]">Scale of 1–{m.scale}</p>
              </div>
              {isAdmin && (
                <button onClick={() => remove(m.id)} data-testid="remove-metric-btn"
                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-[#FF3B30]/10 text-[#86868B] hover:text-[#FF3B30] flex items-center justify-center transition-colors"><Trash2 size={15} /></button>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
